import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import { Bundle, Course, Enrollment, Tag } from '../../types';
import {
  GroupingConfig,
  CatalogShelf,
  groupCourses,
  pivotCourses,
  shelfCardLabel,
  bundlesMatchingTag,
  bundlesSpanningTag,
} from '../../lib/catalogGrouping';
import { compareBundlePrice } from '../../lib/bundlePricing';
import { formatPrice } from '../../lib/format';
import { onThumbError } from '../ui/imageFallback';
import { thumbnailSrc } from '../../lib/imageUrl';
import {
  SchoolPassScope,
  bundleCoveringPass,
  coveringPass,
  hasAllAccessPass,
} from '../../services/schoolPassService';

/**
 * The grouped catalogue view — shelves carry the taxonomy instead of the flat
 * grid. Default: primary-category shelves with secondary-category rows.
 * Pivoted (a secondary tag selected): one shelf for that tag with the primary
 * tags as rows, its matching pack in the header, and spanning packs as the
 * footer upsell. Design + decision record: design-explorations/student-ia-revise/.
 */

const EASE = [0.22, 1, 0.36, 1] as const;

interface GroupedCatalogProps {
  /** The list to RENDER — may already be narrowed by filters / the shelf rule. */
  courses: Course[];
  /**
   * The unreduced tenant course list, used only to classify bundles against
   * the pivot tag. Classifying off the rendered list would flip a
   * wholly-inside pack into a spanning one whenever a member is filtered out.
   */
  allCourses: Course[];
  enrollments: Enrollment[];
  bundles: Bundle[];
  config: GroupingConfig;
  pivotTag: Tag | null;
  /** Whether prices belong on cards (features.sell_courses). */
  showPrice: boolean;
  /** True while any cross-cutting tag filter is active — hides the pack strip. */
  filtering: boolean;
  /** The learner's active school passes — covered items name the school instead of a price. */
  schoolPasses?: SchoolPassScope[];
  /** A pass-holding student sees no prices (shouldHidePrices): paid courses
   *  their pass doesn't cover show no price, and packs it doesn't cover —
   *  which exist only to be bought — are left off entirely. */
  hidePrices?: boolean;
}

const NO_PASSES: SchoolPassScope[] = [];

/** "Covered by <school>" in place of a price, for a course or pack a pass already pays for. */
const CoveredLabel: React.FC<{ school: string; className?: string }> = ({ school, className = '' }) => (
  <span className={`block truncate text-green-600 ${className}`} title={`Covered by ${school}`}>
    Covered by {school}
  </span>
);

/** Compact shelf card: slot chip + trimmed title + price/enrolment state. */
const ShelfCourseCard: React.FC<{
  course: Course;
  contextTags: Array<Tag | null>;
  enrollment?: Enrollment;
  showPrice: boolean;
  coveredBy?: string;
  hidePrice?: boolean;
}> = ({ course, contextTags, enrollment, showPrice, coveredBy, hidePrice }) => {
  const { slot, title } = shelfCardLabel(course.title, contextTags);
  const completed = enrollment?.progress === 100;
  // "Continue" should continue: an in-progress enrolment resumes at the last
  // lesson instead of routing back through the course detail page. The pointer
  // has no FK and can outlive its lesson, so only trust it when the lesson is
  // still in the course — otherwise fall back to the course page.
  const resumeId = enrollment && !completed ? enrollment.lastAccessedLessonId : undefined;
  const resumeExists =
    !!resumeId && course.sections.some((s) => (s.lessons ?? []).some((l) => l.id === resumeId));
  const target = resumeExists
    ? `/learn/${course.id}/lesson/${resumeId}`
    : `/enroll/${course.id}`;
  // The course art returns as a slim strip — recognisability and decoration
  // without disturbing the shelf's scan order: the slot chips stay aligned
  // (now anchored on the art), the title/price hierarchy below is untouched.
  // No art → the original compact card, no placeholder noise.
  const hasArt = !!course.thumbnailUrl;
  return (
    <Link
      to={target}
      className="group flex flex-col overflow-hidden rounded-xl bg-white ring-1 ring-paper-line hover:shadow-card-hover hover:-translate-y-0.5 transition-all"
    >
      {hasArt && (
        <div className="relative h-20 overflow-hidden bg-paper-2">
          {/* Crop biased toward the upper third — that's where the faces are */}
          <img
            src={thumbnailSrc(course.thumbnailUrl)}
            onError={onThumbError}
            alt=""
            aria-hidden
            className="h-full w-full object-cover object-[center_30%] transition-transform duration-500 group-hover:scale-105"
          />
          {slot && (
            <span className="absolute bottom-1.5 left-1.5 inline-flex rounded-md bg-white/95 px-2 py-0.5 text-[10px] font-extrabold tracking-wider text-gray-800 shadow-sm">
              {slot}
            </span>
          )}
          {completed && (
            <span className="absolute right-1.5 top-1.5 rounded-full bg-white/95 p-0.5 shadow-sm">
              <CheckCircle className="h-3.5 w-3.5 text-green-600" aria-label="Completed" />
            </span>
          )}
          {/* In-progress hairline on the strip's bottom edge */}
          {enrollment && !completed && enrollment.progress > 0 && (
            <span className="absolute inset-x-0 bottom-0 h-0.5 bg-black/15">
              <span
                className="block h-full bg-primary-500"
                style={{ width: `${enrollment.progress}%` }}
              />
            </span>
          )}
        </div>
      )}
      <div className="flex flex-1 flex-col p-3">
        {!hasArt && (
          <div className="mb-1.5 flex items-center justify-between">
            {slot ? (
              <span className="inline-flex rounded-md bg-paper-2 px-2 py-0.5 text-[10px] font-extrabold tracking-wider text-gray-700">
                {slot}
              </span>
            ) : (
              <span />
            )}
            {completed ? (
              <CheckCircle className="h-4 w-4 text-green-500" aria-label="Completed" />
            ) : enrollment ? (
              <span className="text-[10px] font-bold text-primary-600">{enrollment.progress}%</span>
            ) : null}
          </div>
        )}
        <div className="text-sm font-semibold leading-snug text-gray-900 group-hover:text-primary-600 transition-colors flex-1">
          {title}
        </div>
        <div className="mt-2 text-xs font-bold">
          {enrollment ? (
            <span className="text-primary-700">
              {completed
                ? 'Completed'
                : enrollment.progress > 0
                  ? `Continue · ${enrollment.progress}%`
                  : 'Continue'}
            </span>
          ) : showPrice && coveredBy ? (
            <CoveredLabel school={coveredBy} />
          ) : showPrice && !(hidePrice && course.price > 0) ? (
            <span className={course.price > 0 ? 'text-gray-900' : 'text-green-600'}>
              {course.price > 0 ? formatPrice(course.price) : 'Free'}
            </span>
          ) : null}
        </div>
      </div>
    </Link>
  );
};

/** Compact pack card for the strip above the shelves (also Home's doorway band). */
export const PackCard: React.FC<{ bundle: Bundle; to?: string; coveredBy?: string }> = ({
  bundle,
  to,
  coveredBy,
}) => {
  const comparison = compareBundlePrice(bundle.price, bundle.courses.map((c) => c.price));
  return (
    <Link
      to={to ?? `/bundle/${bundle.id}`}
      className="group flex items-center justify-between gap-3 rounded-xl bg-white ring-1 ring-paper-line px-4 py-3 hover:shadow-card-hover transition-shadow"
    >
      <div className="min-w-0">
        <div className="text-sm font-bold text-gray-900 truncate group-hover:text-primary-600 transition-colors">
          {bundle.title}
        </div>
        <div className="text-xs text-gray-500">
          {bundle.courses.length} course{bundle.courses.length === 1 ? '' : 's'}
          {bundle.accessDurationDays
            ? (() => {
                const years = Math.round(bundle.accessDurationDays / 365) || 1;
                return ` · ${years} year${years === 1 ? '' : 's'} access`;
              })()
            : ''}
        </div>
      </div>
      {coveredBy ? (
        <div className="min-w-0 max-w-[45%] text-right text-xs font-bold">
          <CoveredLabel school={coveredBy} />
        </div>
      ) : (
        <div className="flex-shrink-0 text-right">
          <div className="text-sm font-bold text-gray-900">{formatPrice(bundle.price)}</div>
          {comparison.verdict === 'saving' && (
            <div className="text-[10px] font-bold text-green-600">SAVE {formatPrice(comparison.saving)}</div>
          )}
        </div>
      )}
    </Link>
  );
};

const RowLabel: React.FC<{ text: string; blurb?: string }> = ({ text, blurb }) => (
  <div className="mt-7 mb-2 first:mt-0 text-[11px] font-extrabold uppercase tracking-[0.12em] text-gray-400">
    {text}
    {blurb && <span className="normal-case font-semibold tracking-normal text-gray-400"> — {blurb}</span>}
  </div>
);

const CardGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">{children}</div>
);

const Shelf: React.FC<{
  shelf: CatalogShelf;
  blurb?: string;
  rowBlurbs?: Record<string, string>;
  enrollmentFor: (courseId: string) => Enrollment | undefined;
  coveredByFor: (courseId: string) => string | undefined;
  showPrice: boolean;
  hidePrice?: boolean;
  headerExtra?: React.ReactNode;
  index: number;
}> = ({ shelf, blurb, rowBlurbs, enrollmentFor, coveredByFor, showPrice, hidePrice, headerExtra, index }) => (
  <motion.section
    className="mb-8 rounded-2xl bg-paper-2 border border-paper-line p-5 sm:p-6"
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: Math.min(index * 0.08, 0.3), ease: EASE }}
  >
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">{shelf.tag.name}</h2>
      <span className="text-sm text-gray-500">
        {shelf.total} {shelf.total === 1 ? 'course' : 'courses'}
      </span>
      {headerExtra && <div className="ml-auto">{headerExtra}</div>}
    </div>
    {blurb && <p className="text-sm text-gray-600 mt-0.5 mb-1">{blurb}</p>}
    {shelf.cells.map((cell, i) => (
      <div key={cell.tag?.id ?? `rest-${i}`}>
        {(cell.tag || shelf.cells.length > 1) && (
          <RowLabel text={cell.tag?.name ?? 'More'} blurb={cell.tag ? rowBlurbs?.[cell.tag.name] : undefined} />
        )}
        {!cell.tag && shelf.cells.length === 1 && <div className="mt-3" />}
        <CardGrid>
          {cell.courses.map((course) => (
            <ShelfCourseCard
              key={course.id}
              course={course}
              contextTags={[shelf.tag, cell.tag]}
              enrollment={enrollmentFor(course.id)}
              coveredBy={coveredByFor(course.id)}
              showPrice={showPrice}
              hidePrice={hidePrice}
            />
          ))}
        </CardGrid>
      </div>
    ))}
  </motion.section>
);

export const GroupedCatalog: React.FC<GroupedCatalogProps> = ({
  courses,
  allCourses,
  enrollments,
  bundles,
  config,
  pivotTag,
  showPrice,
  filtering,
  schoolPasses = NO_PASSES,
  hidePrices = false,
}) => {
  const enrollmentFor = (courseId: string) => enrollments.find((e) => e.courseId === courseId);
  const coveredByFor = (courseId: string) => coveringPass(schoolPasses, courseId)?.schoolName;
  const packCoveredBy = (bundleId: string) => bundleCoveringPass(schoolPasses, bundleId)?.schoolName;
  // An all-access pass already covers every pack, so the strip would be a
  // row of "Covered by" labels with nothing left to get.
  // With prices hidden, a pack is only worth showing if the pass covers it.
  const packVisible = (b: Bundle) => !hidePrices || !!packCoveredBy(b.id);
  const paidBundles = hasAllAccessPass(schoolPasses)
    ? []
    : bundles.filter((b) => b.price > 0 && packVisible(b));

  if (pivotTag) {
    const shelf = pivotCourses(courses, config, pivotTag);
    const packs = bundlesMatchingTag(bundles, allCourses, pivotTag).filter(packVisible);
    const spanning = bundlesSpanningTag(bundles, allCourses, pivotTag).filter(packVisible);
    return (
      <div>
        <Shelf
          shelf={shelf}
          rowBlurbs={config.blurbs}
          enrollmentFor={enrollmentFor}
          coveredByFor={coveredByFor}
          showPrice={showPrice}
          hidePrice={hidePrices}
          index={0}
          headerExtra={
            showPrice && !filtering && packs.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {packs.map((b) => (
                  <Link
                    key={b.id}
                    to={`/bundle/${b.id}`}
                    className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-3.5 py-2 text-xs font-bold text-white hover:bg-primary-700 transition-colors"
                  >
                    {b.title} — {packCoveredBy(b.id) ? `Covered by ${packCoveredBy(b.id)}` : formatPrice(b.price)}
                  </Link>
                ))}
              </div>
            ) : undefined
          }
        />
        {/* The natural next thought after seeing one paper whole: the pack that
            also covers the rest. */}
        {showPrice && !filtering && spanning.length > 0 && (
          <div className="space-y-3">
            {spanning.map((b) => (
              <Link
                key={b.id}
                to={`/bundle/${b.id}`}
                className="flex items-center justify-between gap-4 rounded-2xl bg-paper-2 border border-paper-line px-5 py-4 hover:shadow-card-hover transition-shadow group"
              >
                <div className="text-sm text-gray-700">
                  <span className="font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                    {b.title}
                  </span>{' '}
                  — {b.courses.length} courses{packCoveredBy(b.id) ? '' : ', one payment'}
                </div>
                {packCoveredBy(b.id) ? (
                  <span className="min-w-0 max-w-[45%] text-xs font-bold">
                    <CoveredLabel school={packCoveredBy(b.id)!} />
                  </span>
                ) : (
                  <span className="flex-shrink-0 rounded-lg bg-gray-900 px-3.5 py-2 text-xs font-bold text-white">
                    {formatPrice(b.price)}
                  </span>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  const { shelves, ungrouped } = groupCourses(courses, config);

  return (
    <div>
      {/* Paid packs as a compact strip — legible because the shelves below
          mirror their structure. Free bundles are deliberately absent: their
          members ARE the shelves (see lib/catalogGrouping). */}
      {showPrice && !filtering && paidBundles.length > 0 && (
        <motion.section
          className="mb-8"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: EASE }}
        >
          <h2 className="text-sm font-bold uppercase tracking-[0.12em] text-gray-500 mb-3">
            Get the full set
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {paidBundles.map((b) => (
              <PackCard key={b.id} bundle={b} coveredBy={packCoveredBy(b.id)} />
            ))}
          </div>
        </motion.section>
      )}

      {shelves.map((shelf, i) => (
        <Shelf
          key={shelf.tag.id}
          shelf={shelf}
          blurb={config.blurbs[shelf.tag.name]}
          enrollmentFor={enrollmentFor}
          coveredByFor={coveredByFor}
          showPrice={showPrice}
          hidePrice={hidePrices}
          index={i}
        />
      ))}

      {ungrouped.length > 0 && (
        <section className="mb-8">
          <RowLabel text="More courses" />
          <CardGrid>
            {ungrouped.map((course) => (
              <ShelfCourseCard
                key={course.id}
                course={course}
                contextTags={[]}
                enrollment={enrollmentFor(course.id)}
                coveredBy={coveredByFor(course.id)}
                showPrice={showPrice}
                hidePrice={hidePrices}
              />
            ))}
          </CardGrid>
        </section>
      )}
    </div>
  );
};
